import { Component, computed, HostListener, inject, signal } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TRANSLATION_KEYS } from '../../i18n/translation-keys';
import { SHARED_MODULES } from '../../shared';
import { finalize, of, switchMap, take } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmModal } from '../confirm-modal/confirm-modal';
import { AuthLogin } from '../../services/auth/auth-login';
import { CoreStore } from '../../../core/state/core.store';
import { AuthSession } from '../../../core/auth/auth-session';
import {
  APP_ACCESS_PERMISSIONS,
  APP_ADMIN_PERMISSIONS,
  APP_MANAGE_PERMISSIONS,
  homeForSession,
} from '../../../core/auth/auth-policies';
import { PermissionGate } from '../../directives/permission-gate';

@Component({
  selector: 'app-header',
  imports: [SHARED_MODULES, RouterLink, RouterLinkActive, PermissionGate],
  templateUrl: './header.html',
  styleUrl: './header.scss',
})
export class Header {
  private readonly _translate = inject(TranslateService);
  private readonly _dialog = inject(MatDialog);
  private readonly _authLogin = inject(AuthLogin);
  private readonly _authSession = inject(AuthSession);
  private readonly _authProfile = inject(CoreStore);

  protected readonly translationKeys = TRANSLATION_KEYS;
  protected readonly accessPermissions = APP_ACCESS_PERMISSIONS;
  protected readonly managePermissions = APP_MANAGE_PERMISSIONS;
  protected readonly adminPermissions = APP_ADMIN_PERMISSIONS;
  protected readonly homeUrl = computed(() => homeForSession(this._authSession));
  protected readonly profileMenuOpen = signal(false);

  protected readonly profile = this._authProfile.profile;
  protected readonly userAvatar = computed(() => {
    const profile = this.profile();
    return profile ? `${profile.name.charAt(0)}${profile.surname.charAt(0)}`.toUpperCase() : '--';
  });

  protected toggleProfileMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.profileMenuOpen.update((isOpen) => !isOpen);
  }

  protected closeProfileMenu(): void {
    this.profileMenuOpen.set(false);
  }

  @HostListener('document:click')
  protected closeProfileMenuOnOutsideClick(): void {
    this.closeProfileMenu();
  }

  @HostListener('document:keydown.escape')
  protected closeProfileMenuOnEscape(): void {
    this.closeProfileMenu();
  }

  logOut() {
    this.closeProfileMenu();
    this._dialog
      .open(ConfirmModal, {
        data: {
          title: this._translate.instant(TRANSLATION_KEYS.LOGOUT.TITLE),
          message: this._translate.instant(TRANSLATION_KEYS.LOGOUT.MESSAGE),
          icon: 'logout',
        },
      })
      .afterClosed()
      .pipe(
        switchMap((result) => {
          if (!result) {
            return of(false);
          }
          return this._logOut();
        }),
      )
      .subscribe();
  }

  private _logOut() {
    return this._authLogin.logout().pipe(
      take(1),
      finalize(() => this._authSession.clear()),
    );
  }
}
